import { api, ApiError, OfflineError, setToken } from '../data/api.ts';
import type { AuthStatus } from '../../../shared/protocol.ts';
import { store, wipe } from '../data/db.ts';
import { stopSearch } from '../search/client.ts';
import { clearVaultKey, setVaultKey, withVaultKey } from '../crypto/session.ts';
import { registerPasskey, unlockWithPasskey } from '../crypto/passkey.ts';
import {
  DEFAULT_KDF,
  deriveMaster,
  deriveRecovery,
  newRecoveryPhrase,
  newSalt,
  newVaultKey,
  open,
  seal,
  sodiumReady,
} from '../crypto/vault.ts';

export type SetupResult = { recoveryPhrase: string };

/**
 * Ersteinrichtung. Erzeugt Vault Key und Recovery-Phrase; beides entsteht hier im
 * Browser und wird dem Server nur in gewrappter Form anvertraut.
 */
export async function setupVault(
  email: string,
  passphrase: string,
  setupToken?: string,
): Promise<SetupResult> {
  await sodiumReady();
  const kdfSalt = newSalt();
  const { derived, params } = await deriveMaster(passphrase, kdfSalt, DEFAULT_KDF);

  const recoveryPhrase = await newRecoveryPhrase();
  const recovery = await deriveRecovery(recoveryPhrase);

  const vaultKey = newVaultKey();
  const session = await api.setup({
    email,
    kdfSalt,
    kdfParams: params,
    authKey: derived.authKey,
    recoveryAuthKey: recovery.authKey,
    wrappedVaultKey: seal(vaultKey, derived.wrapKey),
    wrappedVaultKeyRecovery: seal(vaultKey, recovery.wrapKey),
    ...(setupToken ? { setupToken } : {}),
  });

  setToken(session.token);
  setVaultKey(vaultKey);
  await store.setAuth({
    email,
    token: session.token,
    expiresAt: session.expiresAt,
    wrappedVaultKey: session.wrappedVaultKey,
    kdfSalt,
    kdfParams: params,
  });
  return { recoveryPhrase };
}

export class WrongPassphrase extends Error {
  constructor() {
    super('Passphrase stimmt nicht');
    this.name = 'WrongPassphrase';
  }
}

/**
 * Entsperren. Der lokal hinterlegte gewrappte Vault Key macht das auch ohne Netz
 * möglich — die Prüfung der Passphrase ist die geglückte Entschlüsselung selbst.
 */
export async function unlock(passphrase: string): Promise<void> {
  await sodiumReady();
  const stored = await store.getAuth();

  if (stored) {
    const { derived } = await deriveMaster(passphrase, stored.kdfSalt, stored.kdfParams);
    let vaultKey: Uint8Array;
    try {
      vaultKey = open(stored.wrappedVaultKey, derived.wrapKey);
    } catch {
      throw new WrongPassphrase();
    }
    setVaultKey(vaultKey);
    setToken(stored.token);

    // Sitzung im Hintergrund auffrischen; scheitert das, arbeiten wir offline weiter.
    if (stored.expiresAt - Date.now() < 7 * 86_400_000) {
      void api
        .login(derived.authKey)
        .then(async (session) => {
          setToken(session.token);
          await store.setAuth({ ...stored, token: session.token, expiresAt: session.expiresAt });
        })
        .catch(() => undefined);
    }
    return;
  }

  const params = await api.authParams();
  const { derived } = await deriveMaster(passphrase, params.kdfSalt, params.kdfParams);
  let session;
  try {
    session = await api.login(derived.authKey);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) throw new WrongPassphrase();
    throw error;
  }
  const vaultKey = open(session.wrappedVaultKey, derived.wrapKey);
  setVaultKey(vaultKey);
  setToken(session.token);
  const status = await api.authStatus().catch(() => ({ email: '' }));
  await store.setAuth({
    email: status.email ?? '',
    token: session.token,
    expiresAt: session.expiresAt,
    wrappedVaultKey: session.wrappedVaultKey,
    kdfSalt: params.kdfSalt,
    kdfParams: params.kdfParams,
  });
}

/** Zugang über die Recovery-Phrase, wenn die Passphrase verloren ist. */
export async function recoverWithPhrase(phrase: string, newPassphrase: string): Promise<void> {
  await sodiumReady();
  const recovery = await deriveRecovery(phrase);
  const session = await api.recover(recovery.authKey);
  const vaultKey = open(session.wrappedVaultKey, recovery.wrapKey);
  setToken(session.token);
  setVaultKey(vaultKey);

  const kdfSalt = newSalt();
  const { derived, params } = await deriveMaster(newPassphrase, kdfSalt, DEFAULT_KDF);
  const wrappedVaultKey = seal(vaultKey, derived.wrapKey);
  await api.changePassword({
    kdfSalt,
    kdfParams: params,
    authKey: derived.authKey,
    wrappedVaultKey,
  });
  const status = await api.authStatus().catch(() => ({ email: '' }));
  await store.setAuth({
    email: status.email ?? '',
    token: session.token,
    expiresAt: session.expiresAt,
    wrappedVaultKey,
    kdfSalt,
    kdfParams: params,
  });
}

/** Sperren: der Schlüssel verschwindet aus dem Speicher, der Cache bleibt liegen. */
/** Richtet einen Passkey für dieses Gerät ein. Setzt einen entsperrten Vault voraus. */
export async function enablePasskey(): Promise<void> {
  const stored = await store.getAuth();
  const record = await withVaultKey((key) => registerPasskey(stored?.email ?? '', key));
  await store.setPasskey(record);
}

export async function disablePasskey(): Promise<void> {
  await store.clearPasskey();
}

export async function passkeyRegistered(): Promise<boolean> {
  return (await store.getPasskey()) !== undefined;
}

/**
 * Entsperrt über den Passkey statt über die Passphrase. Möglich nur auf einem Gerät,
 * das schon einmal mit der Passphrase geöffnet wurde — die Sitzung stammt von dort.
 */
export async function unlockWithDevicePasskey(): Promise<void> {
  await sodiumReady();
  const record = await store.getPasskey();
  const stored = await store.getAuth();
  if (!record || !stored) throw new Error('Für dieses Gerät ist kein Passkey hinterlegt.');

  setVaultKey(await unlockWithPasskey(record));
  setToken(stored.token);
}

export function lock(): void {
  clearVaultKey();
}

/** Abmelden: zusätzlich wird der lokale Klartext-Cache vollständig entfernt. */
export async function signOut(): Promise<void> {
  clearVaultKey();
  setToken(null);
  stopSearch();
  await wipe();
}

export async function hasLocalVault(): Promise<boolean> {
  return (await store.getAuth()) !== undefined;
}

export async function serverStatus(): Promise<AuthStatus | null> {
  try {
    return await api.authStatus();
  } catch (error) {
    if (error instanceof OfflineError) return null;
    throw error;
  }
}
